'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Check, Star, Zap, Building2, User, ChevronRight, Shield, CreditCard, Smartphone } from 'lucide-react';

interface PricingData {
  individualListingPrice: number;
  agentMonthlyPrice: number;
  currency: string;
}

const INDIVIDUAL_FEATURES = [
  'İlk ilan tamamen ücretsiz',
  'Her ek ilan için tek seferlik ödeme',
  'Abonelik yok, otomatik yenileme yok',
  'İlan yönetimi ve düzenleme',
  'Alıcılarla mesajlaşma',
  'Tur talebi alma',
  'Fotoğraf ve video yükleme',
];

const AGENT_FEATURES = [
  'İlk ilan tamamen ücretsiz',
  'Aktif abonelikle sınırsız ilan',
  'Profesyonel emlakçı profili',
  'Müşteri adayı yönetimi',
  'İlan analitikleri ve performans',
  'Mesajlaşma ve tur yönetimi',
  '3D / 360° ilan desteği',
  'Öncelikli arama sıralaması',
  'Emlakçı portalına tam erişim',
];

const FAQ_ITEMS = [
  {
    q: 'İlk ilan gerçekten ücretsiz mi?',
    a: 'Evet! Her yeni hesap için ilk ilan tamamen ücretsizdir. Bireysel kullanıcılar ve emlakçılar için geçerlidir.',
  },
  {
    q: 'Bireysel ödeme nasıl çalışır?',
    a: 'Ücretsiz ilan hakkınızı kullandıktan sonra, her yeni ilan için 99 TL tek seferlik ödeme yaparsınız. Abonelik yoktur, sadece yayınlamak istediğiniz ilan için ödeme yaparsınız.',
  },
  {
    q: 'Emlakçı aboneliğini iptal edersem ilanlarıma ne olur?',
    a: 'Aboneliğinizi iptal ettiğinizde, mevcut dönem sonuna kadar erişiminiz devam eder. Dönem bitiminde ilanlarınız "Abonelik Gerekli" durumuna geçer, silinmez.',
  },
  {
    q: 'Ödeme güvenli mi?',
    a: 'Evet. Tüm ödemeler Stripe altyapısı üzerinden işlenir. Kart bilgileriniz hiçbir zaman sunucularımızda saklanmaz.',
  },
  {
    q: 'Mobil uygulamada ödeme nasıl yapılır?',
    a: 'iOS uygulamasında Apple In-App Purchase, Android uygulamasında Google Play Billing kullanılır. Web ile aynı hesabınızı kullanabilirsiniz.',
  },
];

export default function PricingPage() {
  const [pricing, setPricing] = useState<PricingData>({
    individualListingPrice: 99,
    agentMonthlyPrice: 499,
    currency: 'TRY',
  });
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    fetch('/api/payments/pricing')
      .then((r) => r.json())
      .then((data) => {
        if (data.individualListingPrice) setPricing(data);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16 lg:pt-18">
        {/* Hero */}
        <section className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-primary-light text-primary text-sm font-bold px-4 py-2 rounded-full mb-6">
              <Zap size={14} />
              Şeffaf Fiyatlandırma
            </div>
            <h1 className="text-4xl lg:text-5xl font-extrabold text-foreground mb-4 leading-tight">
              İlanınızı Yayınlamak<br />
              <span className="text-primary">Hiç Bu Kadar Kolay Olmamıştı</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Her yeni hesap için ilk ilan ücretsiz. Bireysel kullanıcılar için tek seferlik ödeme, emlakçılar için aylık abonelik.
            </p>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
              {/* Individual Card */}
              <div className="card-elevated rounded-3xl overflow-hidden flex flex-col">
                <div className="p-8 border-b border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-primary-light flex items-center justify-center">
                      <User size={22} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-extrabold text-foreground">Bireysel</h2>
                      <p className="text-sm text-muted-foreground">Kişisel ilan sahipleri için</p>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-4xl font-extrabold text-foreground">Ücretsiz</span>
                    </div>
                    <p className="text-sm text-success font-semibold">İlk ilan için</p>
                  </div>
                  <div className="mt-4 p-4 bg-muted rounded-2xl">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-extrabold text-foreground">{pricing.individualListingPrice} TL</span>
                      <span className="text-sm text-muted-foreground">/ ilan</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Sonraki ilanlar için tek seferlik ödeme</p>
                  </div>
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <ul className="space-y-3 flex-1">
                    {INDIVIDUAL_FEATURES.map((f, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-success-light flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check size={11} className="text-success" />
                        </div>
                        <span className="text-sm text-foreground">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    href="/kayit"
                    className="mt-8 btn-secondary w-full text-center py-3 rounded-2xl font-bold text-base flex items-center justify-center gap-2"
                  >
                    Ücretsiz Başla
                    <ChevronRight size={16} />
                  </Link>
                </div>
              </div>

              {/* Agent Card */}
              <div className="card-elevated rounded-3xl overflow-hidden flex flex-col border-2 border-primary relative">
                <div className="absolute top-4 right-4">
                  <span className="flex items-center gap-1 bg-primary text-white text-xs font-bold px-3 py-1.5 rounded-full">
                    <Star size={11} />
                    Profesyonel
                  </span>
                </div>
                <div className="p-8 border-b border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center">
                      <Building2 size={22} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-extrabold text-foreground">Emlakçı</h2>
                      <p className="text-sm text-muted-foreground">Profesyonel gayrimenkul danışmanları için</p>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-4xl font-extrabold text-foreground">Ücretsiz</span>
                    </div>
                    <p className="text-sm text-success font-semibold">İlk ilan için</p>
                  </div>
                  <div className="mt-4 p-4 bg-primary-light rounded-2xl">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-extrabold text-primary">{pricing.agentMonthlyPrice} TL</span>
                      <span className="text-sm text-primary/70">/ ay</span>
                    </div>
                    <p className="text-xs text-primary/70 mt-1">Sonraki ilanlar için aylık abonelik</p>
                  </div>
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <ul className="space-y-3 flex-1">
                    {AGENT_FEATURES.map((f, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check size={11} className="text-white" />
                        </div>
                        <span className="text-sm text-foreground">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    href="/kayit?rol=agent"
                    className="mt-8 btn-primary w-full text-center py-3 rounded-2xl font-bold text-base flex items-center justify-center gap-2"
                  >
                    Emlakçı Olarak Başla
                    <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-12 px-4 bg-muted/40">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: <Shield size={22} className="text-success" />, title: 'Güvenli Ödeme', desc: 'Stripe ile PCI DSS uyumlu ödeme altyapısı' },
                { icon: <CreditCard size={22} className="text-primary" />, title: 'Kart Bilgisi Saklanmaz', desc: 'Kart numaranız hiçbir zaman sunucularımızda tutulmaz' },
                { icon: <Smartphone size={22} className="text-accent" />, title: 'Mobil Uyumlu', desc: 'iOS ve Android uygulamalarında platform içi satın alma' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-border">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <p className="font-bold text-foreground text-sm">{item.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-extrabold text-foreground text-center mb-10">Sık Sorulan Sorular</h2>
            <div className="space-y-3">
              {FAQ_ITEMS.map((item, i) => (
                <div key={i} className="card-elevated rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left"
                  >
                    <span className="font-semibold text-foreground text-sm">{item.q}</span>
                    <ChevronRight
                      size={16}
                      className={`text-muted-foreground transition-transform flex-shrink-0 ml-4 ${openFaq === i ? 'rotate-90' : ''}`}
                    />
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-4">
                      <p className="text-sm text-muted-foreground">{item.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
